import { AddressStep, CategoryStep, ClientStep, InstructionStep, ProductStep, ValidationStep } from '../steps'
import { Box, CircularProgress, Stack, Typography } from '@mui/material'
import { DynamicFlowStepper, DynamicFlowView, useDynamicFlowController } from 'components/common/DynamicFlow'
import { PurchaseFlowStepKey, usePurchaseFlowRealEstateProperty, usePurchaseFlowUtilities, useTargetOrderUser } from './contexts'
import React, { useCallback, useEffect } from 'react'

import { ActionPage } from 'components/common/ActionPage'
import { BillingStep } from '../steps/Billing'
import { Cart } from '../common'
import { EmbedValueProposition } from 'components/common/Embed/EmbedValueProposition/EmbedValueProposition'
import { CONTENT_ROW_MAX_WIDTH, EMBED_CHROME_ATTRIBUTE, purchaseFlowChrome } from './purchaseFlowLayout'
import { ShootingCategory } from 'constants/product'
import { useEmbedConfig } from './contexts/EmbedConfig.context'
import { useEmbedConsent } from './contexts/EmbedConsent.context'
import { useOrderFlowLoaded } from './hooks/useOrderFlowLoaded'
import { resolveOrderFlowFirstScreen } from './hooks/orderFlowFirstScreen'
import { usePurchaseFlowConfig } from './contexts'
import { usePurchaseFlowProducts } from './contexts/PurchaseFlowProducts.context'
import { GRAY_900 } from 'constants/styling/theme'
import { Path } from 'constants/router'
import { PurchaseFlowFooter } from './PurchaseFlowFooter.component'
import { PURCHASE_FLOW_STEP_COLUMN_ATTRIBUTE } from 'utils/analytics/screenEntryLayout'
import { useAuth0 } from 'utils/auth'
import { useCanUserOrder } from 'utils/user/canUserOrder'
import { useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

const stepsWithCart = new Set<string>([
  PurchaseFlowStepKey.PRODUCT,
  PurchaseFlowStepKey.PROPERTY_INFORMATION,
  PurchaseFlowStepKey.FLOOR_PLAN_CERTIFICATION,
  PurchaseFlowStepKey.POWER_OF_ATTORNEY,
  PurchaseFlowStepKey.FLOOR_PLAN_CONFIG,
  PurchaseFlowStepKey.STYLE_SECTION,
  PurchaseFlowStepKey.INSTRUCTIONS,
  PurchaseFlowStepKey.BILLING,
  PurchaseFlowStepKey.VALIDATION,
  PurchaseFlowStepKey.MEASUREMENT_REQUIREMENTS,
  PurchaseFlowStepKey.CONSUMPTION_CERTIFICATE,
  PurchaseFlowStepKey.ENERGY_CERTIFICATE_OF_NEED,
])

/**
 * @component
 * Controller for the purchase flow displaying the steps header and action footer.
 *
 * @example <PurchaseFlowController />
 */
export const PurchaseFlowController: React.FC = () => {
  const { roles } = useAuth0()
  const { isEmbed, setCategoryHint } = useEmbedConfig()
  const { isConsentResolved, showValueProposition, isFirstScreenSettled, dismissValueProposition } = useEmbedConsent()
  const { orderAttemptId, setSelectedCategory, getCategoryConfig } = usePurchaseFlowConfig()
  const { selectedSegment } = usePurchaseFlowProducts()
  const { getPropertyQuery, hasRequestedProperty } = usePurchaseFlowRealEstateProperty()
  const { adminSelectedUserEmail } = useTargetOrderUser()
  const { t } = useTranslation(['purchase_flow'])
  const { exitPurchaseFlow } = usePurchaseFlowUtilities()
  const location = useLocation()
  const exitPurchaseFlowRedirectUrl = location.state?.redirectedFrom
  const canUserOrder = useCanUserOrder()

  const flowCtrl = useDynamicFlowController()

  // Create base PF flow scaffold
  useEffect(() => {

    if (isEmbed) {
      flowCtrl.createEntryNode(
        PurchaseFlowStepKey.PRODUCT,
        {
          component: <ProductStep />,
          hasFooter: true,
          type: 'main',
        }
      )
      flowCtrl.insertAfter(
        PurchaseFlowStepKey.PRODUCT,
        PurchaseFlowStepKey.INSTRUCTIONS,
        {
          component: <InstructionStep />,
          hasFooter: true,
          type: 'main',
        }
      )
      flowCtrl.insertAfter(
        PurchaseFlowStepKey.INSTRUCTIONS,
        PurchaseFlowStepKey.BILLING,
        {
          component: <BillingStep />,
          hasFooter: true,
          type: 'main',
        }
      )
      flowCtrl.insertAfter(
        PurchaseFlowStepKey.BILLING,
        PurchaseFlowStepKey.VALIDATION,
        {
          component: <ValidationStep />,
          hasFooter: true,
          type: 'main',
        }
      )
      return
    }

    if (roles.isAdmin) {
      flowCtrl.createEntryNode(
        PurchaseFlowStepKey.CLIENT,
        {
          component: <ClientStep />,
          hasFooter: true,
          type: 'main',
        }
      )

      flowCtrl.insertAfter(
        PurchaseFlowStepKey.CLIENT,
        PurchaseFlowStepKey.ADDRESS,
        {
          component: <AddressStep />,
          hasFooter: true,
          type: 'main',
        }
      )
    } else {
      flowCtrl.createEntryNode(
        PurchaseFlowStepKey.ADDRESS,
        {
          component: <AddressStep />,
          hasFooter: true,
          type: 'main',
        }
      )
    }

    flowCtrl.insertAfter(
      PurchaseFlowStepKey.ADDRESS,
      PurchaseFlowStepKey.CATEGORY,
      {
        component: <CategoryStep />,
        hasFooter: true,
        type: 'main',
      }
    )
    flowCtrl.insertAfter(
      PurchaseFlowStepKey.CATEGORY,
      PurchaseFlowStepKey.PRODUCT,
      {
        component: <ProductStep />,
        hasFooter: true,
        type: 'main',
      }
    )
    flowCtrl.insertAfter(
      PurchaseFlowStepKey.PRODUCT,
      PurchaseFlowStepKey.INSTRUCTIONS,
      {
        component: <InstructionStep />,
        hasFooter: true,
        type: 'main',
      }
    )
    flowCtrl.insertAfter(
      PurchaseFlowStepKey.INSTRUCTIONS,
      PurchaseFlowStepKey.BILLING,
      {
        component: <BillingStep />,
        hasFooter: true,
        type: 'main',
      }
    )
    flowCtrl.insertAfter(
      PurchaseFlowStepKey.BILLING,
      PurchaseFlowStepKey.VALIDATION,
      {
        component: <ValidationStep />,
        hasFooter: true,
        type: 'main',
      }
    )

    // No dependencies so it runs only once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // In embed mode the category is fixed to on-site real estate (Address/Category steps are skipped)
  useEffect(() => {
    if (isEmbed) setSelectedCategory(ShootingCategory.REAL_ESTATE)
  }, [isEmbed, setSelectedCategory])

  // Embed creates the purchase session here (CategoryStep is skipped); mirrors CategoryStep: admins wait for a target email, isIdle guards single creation
  useEffect(() => {
    if (!isEmbed) return
    if (roles.isAdmin && !adminSelectedUserEmail) return
    if (!getCategoryConfig.isIdle) return

    getCategoryConfig.mutate({ email: adminSelectedUserEmail })
  }, [isEmbed, roles.isAdmin, adminSelectedUserEmail, getCategoryConfig])

  const handleExit = useCallback(async () => {
    const redirectPath = Path.INDEX
    exitPurchaseFlow(true, {
      pathname: exitPurchaseFlowRedirectUrl ? exitPurchaseFlowRedirectUrl : redirectPath,
    })
  }, [exitPurchaseFlow, exitPurchaseFlowRedirectUrl])

  // Check if user can order
  const showNoOrderPermission = !canUserOrder && (roles.isClient || roles.isAdmin)

  // order_flow_loaded (ISSUE-3218) once the first real screen renders: outside the embed that is this mount;
  // in the embed once the consent gate is decided and, for the steps, the property has settled.
  const { ready, firstScreen, failureReason } = resolveOrderFlowFirstScreen({
    isEmbed,
    isFirstScreenSettled,
    showNoOrderPermission,
    showValueProposition,
    hasPropertyRef: hasRequestedProperty,
    isPropertySuccess: getPropertyQuery.isSuccess,
    isPropertyError: getPropertyQuery.isError,
  })
  useOrderFlowLoaded({ surface: 'order_flow', ready, isEmbed, orderAttemptId, firstScreen, failureReason })

  // In embed, the Product step shows a chrome-less gate/property-error (no segment yet, or the property
  // could not be loaded) that has no cart context — suppress the Summary column for those states so the
  // gate/error is centered like the prototype. Non-embed is unaffected.
  const isProductStep = flowCtrl.currentNode?.key === PurchaseFlowStepKey.PRODUCT
  const suppressCartForEmbedGate = isEmbed && isProductStep && (!selectedSegment || getPropertyQuery.isError)

  const { pageContainerPadding, stepColumnPaddingLeft, stepColumnPaddingRight } = purchaseFlowChrome(isEmbed)

  // Embed consent gating (ISSUE-2017). Both branches are embed-only by construction, but the explicit
  // isEmbed guard pins the normal purchase flow to today's render tree no matter what the consent
  // context resolves to. All hooks above still run, so the purchase session keeps being created while
  // the overlay is up and the wizard is ready the moment a service card dismisses it.
  if (isEmbed && !isConsentResolved) {
    return (
      <Stack height="100%" alignItems="center" justifyContent="center">
        <CircularProgress size={48} thickness={4} data-testid="embed-consent-loading" />
      </Stack>
    )
  }

  if (isEmbed && showValueProposition) {
    return (
      <EmbedValueProposition
        onSelect={(hint) => {
          setCategoryHint(hint)
          dismissValueProposition()
        }}
      />
    )
  }

  return (
    <ActionPage
      id="purchase-flow"
      title={t('header_title')}
      customStepper={<DynamicFlowStepper stepNameTranslatorFnc={(key) => t(`steps.${key}`)} />}
      onClose={isEmbed ? undefined : handleExit}
      footerChildren={flowCtrl.currentNode?.hasFooter && <PurchaseFlowFooter />}
    >
      {showNoOrderPermission ? (
        <Stack
          width="100%"
          alignItems="center"
          justifyContent="center"
          height="100%"
          padding="0 4rem"
          boxSizing="border-box"
        >
          <Typography variant="text-lg" fontWeight="medium" color={GRAY_900} textAlign="center">
            {t('no_order_permission')}
          </Typography>
        </Stack>
      ) : (
        <Stack
          width="100%"
          alignItems="center"
          height="100%"
          padding={pageContainerPadding}
          boxSizing="border-box"
          {...(isEmbed ? { [EMBED_CHROME_ATTRIBUTE]: '' } : {})}
        >
          <Stack
            direction="row"
            flexWrap="wrap"
            height="100%"
            width="100%"
            gap={2}
            maxWidth={CONTENT_ROW_MAX_WIDTH}
          >

            <Box
              {...{ [PURCHASE_FLOW_STEP_COLUMN_ATTRIBUTE]: '' }}
              flex="1 0 350px"
              height="100%" sx={{ overflowY: 'auto' }}
              paddingLeft={stepColumnPaddingLeft}
              paddingRight={stepColumnPaddingRight}
              paddingTop={4.8}
              boxSizing="border-box"
              position="relative"
            >
              <DynamicFlowView />
            </Box>

            {!!flowCtrl.currentNode && stepsWithCart.has(flowCtrl.currentNode.key) && !suppressCartForEmbedGate &&
              <Box
                flex="0 0 300px"
                height="100%"
                paddingTop={4.8}
                paddingBottom={2}
                sx={{ overflowY: 'auto' }}
                boxSizing="border-box"
              >
                <Cart />
              </Box>
            }

          </Stack>
        </Stack>
      )}
    </ActionPage>
  )
}
